import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { X, User, CreditCard, Calendar, DollarSign } from 'lucide-react';

interface PagamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pagamentoId?: string;
  onSuccess?: () => void;
}

interface PagamentoFormData {
  alunoId: string;
  valor: number;
  descricao: string;
  formaPagamento: string;
  dataVencimento: string;
  parcelas: string;
  jurosMensal: number;
  jurosFixo: number;
  multaTipo: string;
  multaPercentual: number;
  multaFixa: number;
  descontoTipo: string;
  descontoPercentual: number;
  descontoFixo: number;
  prazoDesconto: string;
  observacoes: string;
  enviarCorreios: boolean;
}

const PagamentoModal: React.FC<PagamentoModalProps> = ({
  isOpen,
  onClose,
  pagamentoId,
  onSuccess
}) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alunos, setAlunos] = useState<any[]>([]);
  const [formData, setFormData] = useState<PagamentoFormData>({
    alunoId: '',
    valor: 0,
    descricao: '',
    formaPagamento: '',
    dataVencimento: '',
    parcelas: '1',
    jurosMensal: 0,
    jurosFixo: 0,
    multaTipo: 'PERCENTAGE',
    multaPercentual: 0,
    multaFixa: 0,
    descontoTipo: 'PERCENTAGE',
    descontoPercentual: 0,
    descontoFixo: 0,
    prazoDesconto: '',
    observacoes: '',
    enviarCorreios: false
  });

  const loadAlunos = React.useCallback(async () => {
    if (!userProfile?.empresaCnpj) return;
    
    try {
      const alunosData = await firebaseService.getAlunos(userProfile.empresaCnpj);
      setAlunos(alunosData);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  }, [userProfile?.empresaCnpj]);

  const loadPagamento = React.useCallback(async () => {
    if (!pagamentoId || !userProfile?.empresaCnpj) return;
    
    try {
      const pagamentos = await firebaseService.getPagamentos(userProfile.empresaCnpj);
      const pagamento = pagamentos.find(p => p.id === pagamentoId);
      
      if (pagamento) {
        setFormData({
          alunoId: pagamento.alunoId,
          valor: pagamento.valor,
          descricao: pagamento.externalReference || '',
          formaPagamento: pagamento.tipoPagamento,
          dataVencimento: pagamento.dataVencimento,
          parcelas: '1',
          jurosMensal: 0,
          jurosFixo: 0,
          multaTipo: 'PERCENTAGE',
          multaPercentual: 0,
          multaFixa: 0,
          descontoTipo: 'PERCENTAGE',
          descontoPercentual: 0,
          descontoFixo: 0,
          prazoDesconto: '',
          observacoes: pagamento.observacoes || '',
          enviarCorreios: false
        });
      }
    } catch (error) {
      console.error('Erro ao carregar pagamento:', error);
    }
  }, [pagamentoId, userProfile?.empresaCnpj]);

  useEffect(() => {
    if (isOpen) {
      loadAlunos();
      if (pagamentoId) {
        loadPagamento();
      }
    }
  }, [isOpen, pagamentoId, loadAlunos, loadPagamento]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);
      
      if (pagamentoId) {
        // Atualizar pagamento existente
        const pagamentos = await firebaseService.getPagamentos(userProfile.empresaCnpj);
        const pagamento = pagamentos.find(p => p.id === pagamentoId);
        
        if (pagamento) {
          await firebaseService.updatePagamentoStatus(
            userProfile.empresaCnpj,
            pagamento,
            'pendente',
            formData.valor
          );
        }
      } else {
        // Criar novo pagamento
        console.log('Criando novo pagamento:', formData);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar pagamento:', error);
      alert('Erro ao salvar pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PagamentoFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calcularParcelas = () => {
    const valorTotal = formData.valor;
    const numParcelas = parseInt(formData.parcelas);
    
    if (numParcelas === 1) {
      return `À vista (${formatCurrency(valorTotal)})`;
    }
    
    const valorParcela = valorTotal / numParcelas;
    return `${numParcelas}x de ${formatCurrency(valorParcela)}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {pagamentoId ? 'Editar Cobrança' : 'Adicionar Cobrança'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="bg-white dark:bg-gray-800 px-6 py-4 max-h-96 overflow-y-auto">
              {/* Dados da cobrança */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Dados da cobrança</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cliente
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <select
                        value={formData.alunoId}
                        onChange={(e) => handleInputChange('alunoId', e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      >
                        <option value="">Selecione o cliente</option>
                        {alunos.map(aluno => (
                          <option key={aluno.id} value={aluno.id}>
                            {aluno.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Forma de pagamento
                    </label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <select
                        value={formData.formaPagamento}
                        onChange={(e) => handleInputChange('formaPagamento', e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      >
                        <option value="">Selecione a forma de pagamento</option>
                        <option value="PIX">Pix</option>
                        <option value="BOLETO">Boleto Bancário</option>
                        <option value="CREDIT_CARD">Cartão de Crédito</option>
                        <option value="DEBIT_CARD">Cartão de Débito</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor da cobrança
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="number"
                        step="0.01"
                        value={formData.valor}
                        onChange={(e) => handleInputChange('valor', parseFloat(e.target.value) || 0)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0,00"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parcelas
                    </label>
                    <select
                      value={formData.parcelas}
                      onChange={(e) => handleInputChange('parcelas', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="1">À vista</option>
                      <option value="2">2x</option>
                      <option value="3">3x</option>
                      <option value="6">6x</option>
                      <option value="12">12x</option>
                    </select>
                    <div className="text-sm text-gray-500 mt-1">
                      {calcularParcelas()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vencimento da cobrança
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="date"
                        value={formData.dataVencimento}
                        onChange={(e) => handleInputChange('dataVencimento', e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Juros e multa */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Juros e multa</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Juros ao mês
                      <span className="text-gray-400 ml-1">i</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={formData.jurosMensal}
                        onChange={(e) => handleInputChange('jurosMensal', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0,00"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor fixo de juros ao mês
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="number"
                        step="0.01"
                        value={formData.jurosFixo}
                        onChange={(e) => handleInputChange('jurosFixo', parseFloat(e.target.value) || 0)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0,00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Multa por atraso
                      <span className="text-gray-400 ml-1">i</span>
                    </label>
                    <select
                      value={formData.multaTipo}
                      onChange={(e) => handleInputChange('multaTipo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="PERCENTAGE">Percentual</option>
                      <option value="FIXED">Valor fixo</option>
                    </select>
                  </div>

                  <div>
                    {formData.multaTipo === 'PERCENTAGE' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor percentual da multa
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={formData.multaPercentual}
                            onChange={(e) => handleInputChange('multaPercentual', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor fixo da multa
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <input
                            type="number"
                            step="0.01"
                            value={formData.multaFixa}
                            onChange={(e) => handleInputChange('multaFixa', parseFloat(e.target.value) || 0)}
                            className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Desconto */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">
                  Desconto
                  <span className="text-gray-400 ml-1">i</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de desconto
                    </label>
                    <select
                      value={formData.descontoTipo}
                      onChange={(e) => handleInputChange('descontoTipo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="PERCENTAGE">Percentual</option>
                      <option value="FIXED">Valor fixo</option>
                    </select>
                  </div>

                  <div>
                    {formData.descontoTipo === 'PERCENTAGE' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor percentual do desconto
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={formData.descontoPercentual}
                            onChange={(e) => handleInputChange('descontoPercentual', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor fixo do desconto
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <input
                            type="number"
                            step="0.01"
                            value={formData.descontoFixo}
                            onChange={(e) => handleInputChange('descontoFixo', parseFloat(e.target.value) || 0)}
                            className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prazo máximo do desconto
                      <span className="text-gray-400 ml-1">i</span>
                    </label>
                    <select
                      value={formData.prazoDesconto}
                      onChange={(e) => handleInputChange('prazoDesconto', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Selecione o prazo de desconto</option>
                      <option value="1">1 dia</option>
                      <option value="3">3 dias</option>
                      <option value="5">5 dias</option>
                      <option value="7">7 dias</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => handleInputChange('observacoes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="A descrição informada será impressa no boleto."
                />
              </div>

              {/* Envio por correios */}
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.enviarCorreios}
                    onChange={(e) => handleInputChange('enviarCorreios', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Quero enviar esta cobrança via Correios ?
                  </span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : 'Adicionar cobrança'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PagamentoModal;
